import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    gridData = "gridData",
    caption = "caption",
    maxHeight = "maxHeight",
    minColumnWidth = "minColumnWidth",
    stickyHeader = "stickyHeader",
    stickyFirstColumn = "stickyFirstColumn",
    zebraStripes = "zebraStripes",
    wrapCellText = "wrapCellText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.gridData]: string;
        [fields.caption]: string;
        [fields.maxHeight]: string;
        [fields.minColumnWidth]: string;
        [fields.stickyHeader]: boolean;
        [fields.stickyFirstColumn]: boolean;
        [fields.zebraStripes]: boolean;
        [fields.wrapCellText]: boolean;
    };
}

type Props = OwnProps & HasShellContext;

interface GridColumn {
    key: string;
    label: string;
}

interface ParsedGrid {
    columns: GridColumn[];
    rows: string[][];
}

const toDisplayValue = (value: unknown) => {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "object") {
        return JSON.stringify(value);
    }
    return String(value);
};

const buildColumns = (header: unknown): GridColumn[] => {
    if (Array.isArray(header)) {
        return header.map((label, index) => ({ key: String(index), label: toDisplayValue(label) }));
    }
    if (header && typeof header === "object") {
        return Object.keys(header).map(key => ({ key, label: toDisplayValue((header as any)[key]) }));
    }
    return [];
};

/**
 * Accepts either `{ "header": {...}, "rows": [...] }` or `[headerObject, ...rows]`.
 * Rows may be objects keyed by the header keys, or positional arrays.
 */
const parseGridData = (rawData: string): { grid?: ParsedGrid; error?: string } => {
    if (!rawData || !rawData.trim()) {
        return {};
    }

    let parsed: any;
    try {
        parsed = JSON.parse(rawData);
    } catch (error) {
        return { error: `Grid Data is not valid JSON. ${(error as Error).message}` };
    }

    let header: unknown;
    let rawRows: unknown;

    if (Array.isArray(parsed)) {
        header = parsed[0];
        rawRows = parsed.slice(1);
    } else if (parsed && typeof parsed === "object") {
        header = parsed.header ?? parsed.headers;
        rawRows = parsed.rows ?? parsed.data ?? parsed.content;
    }

    const columns = buildColumns(header);
    if (columns.length === 0) {
        return { error: 'Grid Data must include a "header" object describing the columns.' };
    }

    if (!Array.isArray(rawRows)) {
        return { error: 'Grid Data must include a "rows" array containing the content.' };
    }

    const rows = rawRows.map(row => {
        if (Array.isArray(row)) {
            return columns.map((_, index) => toDisplayValue(row[index]));
        }
        if (row && typeof row === "object") {
            return columns.map(column => toDisplayValue((row as any)[column.key]));
        }
        return columns.map((_, index) => (index === 0 ? toDisplayValue(row) : ""));
    });

    return { grid: { columns, rows } };
};

export interface SurDataGridStyles {
    wrapper?: InjectableCss;
    caption?: InjectableCss;
    scrollContainer?: InjectableCss;
    message?: InjectableCss;
}

export const surDataGridStyles: SurDataGridStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    caption: {
        css: css`
            display: block;
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-size: ${TYPOGRAPHY.size.larger};
            line-height: ${TYPOGRAPHY.lineHeight.lmedium};
            font-weight: ${TYPOGRAPHY.weight.bold};
            color: ${COLORS.text.main};
            margin-bottom: 12px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.large};
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.large};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    message: {
        css: css`
            display: block;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.smaller};
            color: ${COLORS.error};
            padding: 12px;
            border: 1px dashed ${COLORS.error};
        `,
    },
};

const styles = surDataGridStyles;

const SurDataGrid: React.FunctionComponent<Props> = ({ fields, shellContext }: Props) => {
    const { grid, error } = React.useMemo(() => parseGridData(fields.gridData), [fields.gridData]);

    if (error || !grid) {
        // Only surface authoring problems inside the CMS so the storefront never renders a broken table.
        return shellContext.isInShell ? (
            <StyledWrapper {...styles.message}>{error || "Add Grid Data to display this table."}</StyledWrapper>
        ) : null;
    }

    const maxHeight = fields.maxHeight?.trim() || "480px";
    const minColumnWidth = fields.minColumnWidth?.trim() || "160px";
    const headerZIndex = fields.stickyHeader ? 3 : 1;

    const scrollContainer = {
        css: css`
            width: 100%;
            max-height: ${maxHeight};
            overflow: auto;
            /* keeps momentum scrolling on iOS */
            -webkit-overflow-scrolling: touch;
            border: 1px solid ${COLORS.border.main};
            background: ${COLORS.background.main};

            &:focus-visible {
                outline: 2px solid ${COLORS.outline};
                outline-offset: 2px;
            }

            table {
                border-collapse: separate;
                border-spacing: 0;
                width: max-content;
                min-width: 100%;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${COLORS.text.main};
            }

            th,
            td {
                min-width: ${minColumnWidth};
                padding: 12px 16px;
                text-align: left;
                vertical-align: top;
                border-bottom: 1px solid ${COLORS.border.main};
                white-space: ${fields.wrapCellText ? "normal" : "nowrap"};
            }

            thead th {
                background: ${COLORS.background.light};
                font-weight: ${TYPOGRAPHY.weight.bold};
                ${fields.stickyHeader
                    ? css`
                          position: sticky;
                          top: 0;
                          z-index: 2;
                      `
                    : ""}
            }

            ${fields.zebraStripes
                ? css`
                      tbody tr:nth-child(even) td {
                          background: ${COLORS.background.lighter};
                      }
                  `
                : ""}

            ${fields.stickyFirstColumn
                ? css`
                      tbody td:first-child {
                          position: sticky;
                          left: 0;
                          z-index: 1;
                          background: ${COLORS.background.main};
                          border-right: 1px solid ${COLORS.border.main};
                      }

                      tbody tr:nth-child(even) td:first-child {
                          background: ${fields.zebraStripes ? COLORS.background.lighter : COLORS.background.main};
                      }

                      thead th:first-child {
                          left: 0;
                          position: sticky;
                          z-index: ${headerZIndex};
                          border-right: 1px solid ${COLORS.border.main};
                      }
                  `
                : ""}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-height: 360px;
                        th,
                        td {
                            padding: 10px 12px;
                            font-size: ${TYPOGRAPHY.size.tiny};
                        }
                    `,
                    css`
                        max-height: 400px;
                        th,
                        td {
                            padding: 10px 12px;
                        }
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.caption && <StyledWrapper {...styles.caption}>{fields.caption}</StyledWrapper>}
            <StyledWrapper
                {...scrollContainer}
                tabIndex={0}
                role="region"
                aria-label={fields.caption || "Data grid"}
            >
                <table>
                    <thead>
                        <tr>
                            {grid.columns.map(column => (
                                <th key={column.key} scope="col">
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {grid.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                    <td key={grid.columns[cellIndex].key}>{cell}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultGridData = `{
    "header": {
        "event": "Upcoming Events",
        "dates": "Dates",
        "location": "Locations"
    },
    "rows": [
        {
            "event": "Irish Furniture and Homeware Show",
            "dates": "23 - 25 August 2026",
            "location": "Dublin, Ireland"
        },
        {
            "event": "INDX Furniture Show",
            "dates": "26 - 27 August 2026",
            "location": "Birmingham, England"
        },
        {
            "event": "INDX Homeware Show",
            "dates": "8 - 9 September 2026",
            "location": "Birmingham, England"
        },
        {
            "event": "Decorex",
            "dates": "11 - 14 October 2026",
            "location": "London, England"
        },
        {
            "event": "January Furniture Show",
            "dates": "24 - 27 January 2027",
            "location": "Birmingham, England"
        }
    ]
}`;

const widgetModule: WidgetModule = {
    component: withIsInShell(SurDataGrid),
    definition: {
        group: "Basic",
        displayName: "Sur Data Grid",
        icon: "list",
        fieldDefinitions: [
            {
                name: fields.caption,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.gridData,
                displayName: "Grid Data (JSON)",
                editorTemplate: "CodeField",
                defaultValue: defaultGridData,
                fieldType: "General",
                sortOrder: 1,
                tooltip:
                    'Provide { "header": { key: label }, "rows": [ { key: value } ] }. Rows may also be arrays of values.',
                options: {
                    mode: { name: "javascript", json: true },
                    lineNumbers: true,
                    autoRefresh: true,
                },
                validate: value => parseGridData(value ?? "").error ?? "",
            },
            {
                name: fields.maxHeight,
                displayName: "Max Height",
                editorTemplate: "TextField",
                defaultValue: "480px",
                fieldType: "General",
                sortOrder: 2,
                tooltip: "CSS height that triggers vertical scrolling, for example 480px or 60vh.",
            },
            {
                name: fields.minColumnWidth,
                displayName: "Min Column Width",
                editorTemplate: "TextField",
                defaultValue: "160px",
                fieldType: "General",
                sortOrder: 3,
                tooltip: "CSS width per column that triggers horizontal scrolling on narrow screens.",
            },
            {
                name: fields.stickyHeader,
                displayName: "Freeze Header Row",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.stickyFirstColumn,
                displayName: "Freeze First Column",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.zebraStripes,
                displayName: "Alternate Row Shading",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.wrapCellText,
                displayName: "Wrap Cell Text",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
        ],
    },
};

export default widgetModule;
